import {Box, HBox} from '@buncha/components/core/view';
import {ClientType, getStoreName} from '@buncha/common/types/common';
import {
  FlatBarcode,
  ScannerMode,
} from '@buncha/components/composites/Scanner/type';
import {H5Text, H6Text} from '@buncha/components/core/text';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import {OrderItemStatus, SubstitutionItemParams} from '../types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {formatCurrency, isItemOrganic, isWeb} from '@buncha/utils/common';
import {
  getDoesBarcodeMatch,
  isNSC2,
  isPlu,
  removeLeadingZeroes,
} from '../utils/ScannedAddItem';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';
import {
  useScannedAddItem,
  useUpdateOrderItemStatus,
} from '../hooks/ScannedAddItem';

import {AddB2BItemParams} from '../types/ScannedAddItem';
import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import BarcodeIcon from '@buncha/components/core/icon/BarcodeIcon';
import BarcodeList from '@buncha/itemDetails/components/NSCItemScanner/BarcodeList';
import {CustomImage} from '@buncha/components/core/Image';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import ItemTypePill from '../component/ItemName/ItemTypePill';
import {ModalDetails} from '@buncha/onBoarding/types';
import {Modules} from '@buncha/utils/types';
import NSCSkipModal from '@buncha/itemDetails/components/ItemScanner/NSCSkipModal';
import {PButton} from '@buncha/components/core/Button';
import QuantitySelector from '@buncha/components/composites/QuantitySelector';
import Scanner from '@buncha/components/composites/Scanner';
import ScreenHeader from '@buncha/common/components/screenHeader';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {ToastType} from '@buncha/components/composites/notification/type';
import UnableToScanSheet from '../components/UnableToScanSheet';
import {b2bNav} from '@buncha/B2B/navigation';
import font from '@buncha/appStyles/style/font';
import {navigation} from '@buncha/config/navigation';
import {styles} from '../styles/ScannedAddItemPage';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';
import {useSubstituteItemInRun} from '@buncha/itemDetails/hooks/itemReplacement';
import {useToastMessage} from '@buncha/components/composites/notification';

const ScannedAddItemPage = () => {
  const theme = useColorThemeType('main');
  const muliFont = useFontTheme('muli');
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const constants = useAppSelector(gState => gState.onboarding.constants);

  const skipFlow = constants?.SKIP_B2B_UPC_FLOW === 'true';

  const routeParams: any = useRoute()?.params;

  const initialPlu = routeParams?.plu;
  const [isActive, setIsActive] = useState(false);
  const [quantity, setQuantity] = useState(routeParams?.quantity ?? 1);
  const addAnotherBarcode = useRef(false);
  const isNSC2Item = useRef(false);
  const itemUpc = useRef<string[]>([]);
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const substituteParams: SubstitutionItemParams =
    routeParams?.substituteParams;
  const orderItemId = routeParams?.orderItemId;
  const {substituteItemInRun, itemSubstituteLoading} =
    useSubstituteItemInRun(true);
  const {changeOrderItemStatus, changingStatus} = useUpdateOrderItemStatus();
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [triggerAddAnother, setTriggerAddAnother] = useState<boolean>(true);
  const modalShown = useRef(false);
  const skipModalBarcode = useRef<FlatBarcode>();
  const ALLOW_SHOPPER_NON_NSC_ITEM_MODAL =
    constants?.ALLOW_SHOPPER_NON_NSC_ITEM_MODAL;

  const {
    item,
    loading,
    addItemLoading,
    addScannedItem,
    scannedItemFetch,
    clearItem,
    clearScannedPlu,
  } = useScannedAddItem(
    initialPlu,
    substituteParams || orderItemId ? routeParams?.orderId : undefined,
  );

  const onScanHandler = useCallback(
    async (barcode?: FlatBarcode, _base64?: string, plu?: string) => {
      let slicedBarcode =
        barcode?.data && isNSC2(barcode?.data)
          ? barcode?.data
          : barcode?.data?.slice?.(0, barcode?.data?.length - 1);
      if (!addAnotherBarcode.current) {
        if (barcode?.data && isNSC2(barcode?.data))
          return showErrorToast(
            "For weighted items, please proceed by entering the 'PLU Number' after selecting 'Unable to Scan Item'.",
          );
        const res = await scannedItemFetch(
          routeParams?.storeIndex,
          slicedBarcode,
          plu,
          skipFlow,
        );
        if (res) addAnotherBarcode.current = true;
        if (res && slicedBarcode)
          setBarcodes(b => [...b, removeLeadingZeroes(slicedBarcode)]);
        if (!res) setIsActive(true);
      } else if (
        (isNSC2Item.current && barcode?.data && isNSC2(barcode?.data)) ||
        (slicedBarcode &&
          getDoesBarcodeMatch(itemUpc.current, slicedBarcode)) ||
        (!isNSC2Item.current && skipFlow) ||
        modalShown.current
      ) {
        if (slicedBarcode)
          setBarcodes(b => [...b, removeLeadingZeroes(slicedBarcode)]);
      } else {
        let modalDetails: ModalDetails | undefined;
        try {
          modalDetails = JSON.parse(ALLOW_SHOPPER_NON_NSC_ITEM_MODAL ?? '{}');
        } catch {
          modalDetails = undefined;
        }
        if (
          !isNSC2Item.current &&
          (modalDetails?.header?.length || modalDetails?.subheader?.length)
        ) {
          skipModalBarcode.current = barcode;
          return setSkipModalOpen(true);
        }

        setIsActive(true);
        showErrorToast(body?.itemNotFoundToast);
      }
      if (plu) setIsActive(true);
    },
    [
      ALLOW_SHOPPER_NON_NSC_ITEM_MODAL,
      routeParams?.storeIndex,
      scannedItemFetch,
      showErrorToast,
      skipFlow,
    ],
  );

  const closeSkipModal = useCallback(() => {
    setSkipModalOpen(false);
    setIsActive(true);
  }, []);

  const skipModalBottomCTAhandler = useCallback(() => {
    modalShown.current = true;
    onScanHandler(skipModalBarcode.current);
    setSkipModalOpen(false);
  }, [onScanHandler]);

  const addAnother = useCallback(() => {
    clearItem();
    clearScannedPlu();
    setIsActive(true);
    addAnotherBarcode.current = false;
    setBarcodes([]);
  }, [clearItem, clearScannedPlu]);

  const addOtherBarcode = useCallback(() => {
    addAnotherBarcode.current = true;
    Analytics.eventWithProps('Add more barcodes', {
      type: EventType.cta,
      cleint: ClientType.meijer,
    });
    setIsActive(true);
    setTriggerAddAnother(prev => !prev);
  }, []);

  const removeBarcode = useCallback(
    (barcode: string) => {
      const i = barcodes.findIndex(el => el === barcode);
      setBarcodes([...barcodes.slice(0, i), ...barcodes.slice(i + 1)]);
    },
    [barcodes],
  );

  const handleOnPress = useCallback(async () => {
    if (!item || !barcodes?.length) return;
    let res;
    if (substituteParams) {
      substituteParams.runId = routeParams?.runId;
      substituteParams.upcInput = {
        upcCodes: barcodes ?? [],
        upcOriginal: item.upc,
      };
      res = await substituteItemInRun({
        ...substituteParams,
        runId: routeParams?.runId,
        upcInput: {
          upcCodes: barcodes ?? [],
          upcOriginal: item.upc,
        },
        quantity: quantity,
      });
      if (res)
        Analytics.eventWithProps('Add Substitute', {
          type: EventType.cta,
          source: 'Item Scanner Meijer Page',
          runId: routeParams?.runId,
          storeType: getStoreName(String(routeParams?.storeId)),
          countOfBarcodes: barcodes.length,
          client: ClientType.meijer,
        });
    } else if (routeParams?.orderItemId) {
      const substituteData: SubstitutionItemParams = {
        catalogItemId: item.id,
        isCustomItem: false,
        itemImageUrl: item.itemImageUrl,
        name: item.name,
        orderId: routeParams?.orderId,
        position: 0,
        orderItemId: routeParams?.orderItemId,
        price: item.price,
        quantity: quantity, // change to dynamic if required
        substitutedBy: JSON.stringify(item),
        upcInput: {
          upcCodes: barcodes ?? [],
          upcOriginal: item.upc,
        },
        runId: routeParams?.runId,
      };
      res = await substituteItemInRun(substituteData);
      if (res)
        Analytics.eventWithProps('Add Substitute', {
          type: EventType.cta,
          source: 'Item Scanner Meijer Page',
          runId: routeParams?.runId,
          storeType: getStoreName(String(routeParams?.storeId)),
          countOfBarcodes: barcodes.length,
          client: ClientType.meijer,
        });
    } else {
      const params: AddB2BItemParams = {
        catalogItemId: item?.id,
        isCustomItem: false,
        itemImageUrl: item?.itemImageUrl,
        name: item?.name,
        price: item?.price,
        priceWithTax: item?.price,
        quantity: quantity,
        orderId: routeParams?.orderId,
        upcOriginal: item?.upc,
        upcCodes: barcodes ?? [],
        tags: item?.tags?.length ? item?.tags?.[0] : '',
        itemUnit: item?.unit,
        runId: routeParams?.runId,
        position: 0,
        priceSensitive: false,
        weightSensitive: false,
      };
      res = await addScannedItem(params);
    }
    if (res) {
      Analytics.eventWithProps('Barcode proceed', {
        source: substituteParams || orderItemId ? 'Substitution' : 'Addition',
        client: ClientType.meijer,
        countOfBarcodes: barcodes.length,
      });
      if (routeParams?.quantity !== quantity)
        Analytics.eventWithProps('Update quantity', {
          source: 'Item Scanner Meijer Page',
          client: ClientType.meijer,
          previousQuantity: routeParams?.quantity,
          quantity: quantity,
        });

      if (routeParams?.returnPage === B2BScreens.B2BCheckout) {
        await changeOrderItemStatus(
          [res?.id],
          OrderItemStatus.packed,
          routeParams?.runId,
        );
        return b2bNav.B2BCheckout({
          runId: routeParams?.runId,
          storeId: routeParams?.storeId,
          storeIndex: routeParams?.storeIndex,
        });
      }

      if (routeParams?.returnPage === ShopperScreens.OrderPackingPage)
        return navigation.navigate(HomeScreens.Shopper, {
          screen: ShopperScreens.OrderPackingPage,
          params: {runId: routeParams?.runId},
        });
      navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.BatchDetailsPage,
        params: {runId: routeParams?.runId},
      });
    }
  }, [
    addScannedItem,
    barcodes,
    changeOrderItemStatus,
    item,
    orderItemId,
    quantity,
    routeParams?.orderId,
    routeParams?.orderItemId,
    routeParams?.quantity,
    routeParams?.returnPage,
    routeParams?.runId,
    routeParams?.storeId,
    routeParams?.storeIndex,
    substituteItemInRun,
    substituteParams,
  ]);

  useEffect(() => {
    isNSC2Item.current = Boolean(
      item?.upc && (isNSC2(item?.upc) || isPlu(item?.upc)),
    );
    itemUpc.current = item?.secondaryBarcode ?? [];
  }, [item?.secondaryBarcode, item?.upc]);

  useEffect(() => {
    if (!barcodes?.length) setIsActive(true);
  }, [barcodes]);

  useEffect(() => {
    if (initialPlu) onScanHandler(undefined, undefined, initialPlu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Header
      style={styles.parentBox}
      headerColor={theme?.background?.blue?.[300]}
      showDefaultLeftHeader>
      <ScreenHeader
        title={`${body?.scan?.[0]}${
          substituteParams || orderItemId ? body?.scan?.[1] : body?.scan?.[2]
        }`}
      />
      <Box style={styles.scannerContainer}>
        <If condition={!isWeb()}>
          <UnableToScanSheet
            addAnother={addAnother}
            setScanner={setIsActive}
            findItem={onScanHandler}
            itemName={item?.name}
            price={item?.price}
          />
        </If>
        <Scanner
          mode={ScannerMode.singleScan}
          onScan={onScanHandler}
          scannerEnabled={isActive}
          setScannerEnabled={setIsActive}
          triggerAddAnother={triggerAddAnother}
        />
      </Box>
      <If condition={isNSC2Item.current}>
        <BarcodeList barcodes={barcodes} removeBarcode={removeBarcode} />
      </If>
      <Box style={styles.detailsContainer}>
        <If condition={loading}>
          <Box style={styles.loadingBox}>
            <SpinnerComponent />
            <H6Text>{body?.fetching}</H6Text>
            <H6Text>{body?.bePatient}</H6Text>
          </Box>
        </If>
        <If condition={!loading}>
          <If condition={!item}>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {body?.tip}
            </H6Text>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  color: theme?.text?.gray?.[100],
                },
              ]}>
              {body?.scanBarcode}
            </H6Text>
          </If>
          <If condition={item}>
            <Box style={styles.details}>
              <If condition={!barcodes?.length}>
                <H5Text
                  style={{
                    color: theme?.text?.blue?.[300],
                    fontFamily: muliFont?.[900]?.normal,
                  }}>
                  {body?.scanAddedItem}
                </H5Text>
                <Divider style={styles.divider} />
              </If>
              <HBox style={styles.itemDetailsContainer}>
                <CustomImage
                  source={item?.itemImageUrl ?? ''}
                  style={[styles.imageStyle]}
                  resizeMode={'contain'}
                />
                <Box style={styles.itemText}>
                  <ItemTypePill
                    itemType={item?.itemType}
                    isOrganic={isItemOrganic(item?.name || '')}
                  />
                  <H5Text
                    numberOfLines={2}
                    style={[
                      styles.textAlignLeft,
                      {
                        color: theme?.text?.blue?.[300],
                      },
                    ]}>
                    {item?.name}
                  </H5Text>
                  <H6Text
                    style={[
                      styles.textAlignLeft,
                      {
                        color: theme?.text?.blue?.[300],
                      },
                    ]}>
                    {body?.estPrice}
                    {formatCurrency(item?.price ?? 0, true)}
                  </H6Text>
                  <If
                    condition={Boolean(
                      item?.upc && !(isNSC2(item?.upc) || isPlu(item?.upc)),
                    )}>
                    <HBox style={styles.barcodeBox}>
                      <BarcodeIcon fill={theme?.text?.blue?.[300]} />
                      <H6Text
                        style={{
                          color: theme?.text?.blue?.[300],
                        }}>
                        {item?.upc}
                      </H6Text>
                    </HBox>
                  </If>
                </Box>
                <QuantitySelector
                  height={50}
                  width={120}
                  quantity={quantity}
                  onQuantityUpdate={setQuantity}
                  allowQunatityToZero={false}
                  maxQuantity={10}
                  minQuantity={1}
                  onDelete={addAnother}
                  disableDecrement={initialPlu && quantity === 1}
                />
              </HBox>
              <HBox style={styles.buttonsContainer}>
                <PButton
                  title={button?.addBarcodes}
                  numberOfLines={2}
                  titleStyle={{
                    fontSize: font.FontSize.fontH5.fontSize,
                  }}
                  style={styles.bottomButton}
                  containerStyle={styles.buttonContainer}
                  titleColor={theme?.text?.blue?.[100]}
                  borderWidth={1}
                  borderColor={theme?.text?.blue?.[100]}
                  bgColor={theme?.background.white[100]}
                  onPress={addOtherBarcode}
                  disabled={
                    addItemLoading ||
                    !isNSC2Item.current ||
                    !barcodes?.length ||
                    itemSubstituteLoading
                  }
                />
                <PButton
                  title={button?.proceed}
                  style={styles.bottomButton}
                  containerStyle={styles.buttonContainer}
                  titleColor={theme?.text?.white?.[100]}
                  borderWidth={1}
                  borderColor={theme?.text?.white?.[100]}
                  bgColor={theme?.background.blue[100]}
                  onPress={handleOnPress}
                  loadingIndicator={
                    <SpinnerComponent style={styles.loadingComponent} />
                  }
                  changeStyleOnLoading
                  disabledBgColor={theme?.background.blue[100]}
                  disabled={
                    addItemLoading || !barcodes?.length || itemSubstituteLoading
                  }
                  isLoading={
                    addItemLoading || itemSubstituteLoading || changingStatus
                  }
                />
              </HBox>
            </Box>
          </If>
        </If>
      </Box>
      <NSCSkipModal
        open={skipModalOpen}
        bottomCTAHandler={skipModalBottomCTAhandler}
        topCTAHandler={closeSkipModal}
        expectedBarcode={itemUpc.current?.[0] ?? ''}
        scannedBarcode={skipModalBarcode.current?.data ?? ''}
        itemId={item?.id}
        itemName={item?.name}
        orderId={routeParams?.orderId}
        runId={routeParams?.runId}
      />
    </Header>
  );
};

export default React.memo(ScannedAddItemPage);
